import { useState, useEffect, useCallback } from 'react';
import useDebounce from './useDebounce';
import useProjectsStoreRepository from '../store/projectsStoreRepository';
import { employeesApi } from '../services/api';
import { PROJECT_STATUSES } from '../constants/projectStatuses';
import { CLIENT_PARTY_TYPES } from '../constants/clientPartyTypes';
import { PROJECT_SCOPE_TYPES } from '../constants/projectScopeTypes';

const useProjectsRepositorySearch = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedBusinessAreas, setSelectedBusinessAreas] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [selectedProjectStatus, setSelectedProjectStatus] = useState([PROJECT_STATUSES.ACTIVE]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedClientPartyTypes, setSelectedClientPartyTypes] = useState([CLIENT_PARTY_TYPES.EXTERNAL, CLIENT_PARTY_TYPES.INTERNAL]);
  const [selectedScopeTypes, setSelectedScopeTypes] = useState([PROJECT_SCOPE_TYPES.LOCAL, PROJECT_SCOPE_TYPES.GLOBAL]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [includeHierarchyProjects, setIncludeHierarchyProjects] = useState(false);

  const debouncedSearchTerm = useDebounce(searchInputValue, 500);

  const {
    searchQuery,
    setSearchQuery,
    setScrollPosition,
    setLastCardId,
    setIsReturningFromDetails,
    setSelectedBusinessAreas: setStoreSelectedBusinessAreas,
    setSelectedClients: setStoreSelectedClients,
    setSelectedBusinessUnits: setStoreSelectedBusinessUnits,
    setSelectedProjectStatus: setStoreSelectedProjectStatus,
    setSelectedCluster: setStoreSelectedCluster,
    setSelectedClientPartyTypes: setStoreSelectedClientPartyTypes,
    setSelectedScopeTypes: setStoreSelectedScopeTypes,
    setSelectedManager: setStoreSelectedManager,
    setIncludeHierarchyProjects: setStoreIncludeHierarchyProjects,
    accessibleProjectIds,
    setAccessibleProjectIds,
    isLoadingAccessibleProjects,
    setIsLoadingAccessibleProjects,
    uiSearchInput,
    setUISearchInput,
  } = useProjectsStoreRepository();

  // Synchronize UI with store on mount (only for search input)
  useEffect(() => {
    if (uiSearchInput !== undefined && uiSearchInput !== searchInputValue) {
      setSearchInputValue(uiSearchInput);
    }
  }, [uiSearchInput]);

  // Keep the store's uiSearchInput in sync with local state
  useEffect(() => {
    setUISearchInput(searchInputValue);
  }, [searchInputValue, setUISearchInput]);

  // Handle business areas change
  useEffect(() => {
    // Reset scroll position when business areas change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store (no refetch needed - client-side filtering)
    setStoreSelectedBusinessAreas(selectedBusinessAreas);
  }, [selectedBusinessAreas, setStoreSelectedBusinessAreas, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle clients change
  useEffect(() => {
    // Reset scroll position when clients change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedClients(selectedClients);
  }, [selectedClients, setStoreSelectedClients, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle business units change
  useEffect(() => {
    // Reset scroll position when business units change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedBusinessUnits(selectedBusinessUnits);
  }, [selectedBusinessUnits, setStoreSelectedBusinessUnits, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle project status change
  useEffect(() => {
    // Reset scroll position when project status changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedProjectStatus(selectedProjectStatus);
  }, [selectedProjectStatus, setStoreSelectedProjectStatus, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle cluster change
  useEffect(() => {
    // Reset scroll position when cluster changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedCluster(selectedCluster);
  }, [selectedCluster, setStoreSelectedCluster, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle client party types change
  useEffect(() => {
    // Reset scroll position when client party types change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedClientPartyTypes(selectedClientPartyTypes);
  }, [selectedClientPartyTypes, setStoreSelectedClientPartyTypes, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle scope types change
  useEffect(() => {
    // Reset scroll position when scope types change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedScopeTypes(selectedScopeTypes);
  }, [selectedScopeTypes, setStoreSelectedScopeTypes, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle manager change
  useEffect(() => {
    // Reset scroll position when manager changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update store
    setStoreSelectedManager(selectedManager);

    // Auto-uncheck hierarchy checkbox when manager is cleared
    if (!selectedManager && includeHierarchyProjects) {
      setIncludeHierarchyProjects(false);
      setStoreIncludeHierarchyProjects(false);
    }
  }, [selectedManager, setStoreSelectedManager, setScrollPosition, setLastCardId, setIsReturningFromDetails, includeHierarchyProjects, setStoreIncludeHierarchyProjects]);

  // Handle include hierarchy projects change
  useEffect(() => {
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    setStoreIncludeHierarchyProjects(includeHierarchyProjects);

    if (!includeHierarchyProjects) {
      setAccessibleProjectIds(null);
    }
  }, [includeHierarchyProjects, setStoreIncludeHierarchyProjects, setScrollPosition, setLastCardId, setIsReturningFromDetails, setAccessibleProjectIds]);

  // Fetch accessible projects when manager is selected and checkbox is checked
  useEffect(() => {
    const fetchAccessibleProjects = async () => {
      if (!selectedManager || !includeHierarchyProjects) {
        return;
      }

      setIsLoadingAccessibleProjects(true);
      try {
        const projects = await employeesApi.getAccessibleProjects(selectedManager.id);
        const projectIds = new Set(projects.map(p => p.id));
        setAccessibleProjectIds(projectIds);
      } catch (error) {
        console.error('Failed to fetch accessible projects:', error);
        setAccessibleProjectIds(new Set());
      } finally {
        setIsLoadingAccessibleProjects(false);
      }
    };

    fetchAccessibleProjects();
  }, [selectedManager, includeHierarchyProjects, setAccessibleProjectIds, setIsLoadingAccessibleProjects]);

  // Handle search input with debounce (like CV Repository)
  useEffect(() => {
    // Skip the initial render when both terms are empty
    if (debouncedSearchTerm === '' && searchQuery === '' && searchInputValue === '') {
      return;
    }

    // Only proceed if the debounced term has actually changed
    if (debouncedSearchTerm === searchQuery) {
      return;
    }

    // Reset scroll position when actively searching
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);

    // Update search query in store (no refetch needed - client-side filtering)
    setSearchQuery(debouncedSearchTerm);
  }, [debouncedSearchTerm, searchQuery, searchInputValue, setSearchQuery, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Method to clear search filters (like CV Repository)
  const clearSearchFilters = useCallback(() => {
    setSearchInputValue('');
    setSearchQuery('');
    setSelectedBusinessAreas([]);
    setSelectedClients([]);
    setSelectedBusinessUnits([]);
    setSelectedProjectStatus([PROJECT_STATUSES.ACTIVE]);
    setSelectedCluster('');
    setSelectedClientPartyTypes([CLIENT_PARTY_TYPES.EXTERNAL, CLIENT_PARTY_TYPES.INTERNAL]);
    setSelectedScopeTypes([PROJECT_SCOPE_TYPES.LOCAL, PROJECT_SCOPE_TYPES.GLOBAL]);
    setSelectedManager(null);
    setIncludeHierarchyProjects(false);

    // Also update store state
    setStoreSelectedBusinessAreas([]);
    setStoreSelectedClients([]);
    setStoreSelectedBusinessUnits([]);
    setStoreSelectedProjectStatus([PROJECT_STATUSES.ACTIVE]);
    setStoreSelectedCluster('');
    setStoreSelectedClientPartyTypes([CLIENT_PARTY_TYPES.EXTERNAL, CLIENT_PARTY_TYPES.INTERNAL]);
    setStoreSelectedScopeTypes([PROJECT_SCOPE_TYPES.LOCAL, PROJECT_SCOPE_TYPES.GLOBAL]);
    setStoreSelectedManager(null);
    setStoreIncludeHierarchyProjects(false);
    setAccessibleProjectIds(null);
  }, [
    setSearchQuery,
    setStoreSelectedBusinessAreas,
    setStoreSelectedClients,
    setStoreSelectedBusinessUnits,
    setStoreSelectedProjectStatus,
    setStoreSelectedCluster,
    setStoreSelectedClientPartyTypes,
    setStoreSelectedScopeTypes,
    setStoreSelectedManager,
    setStoreIncludeHierarchyProjects,
    setAccessibleProjectIds
  ]);

  return {
    // Values
    searchInputValue,
    selectedBusinessAreas,
    selectedClients,
    selectedBusinessUnits,
    selectedProjectStatus,
    selectedCluster,
    selectedClientPartyTypes,
    selectedScopeTypes,
    selectedManager,
    includeHierarchyProjects,
    accessibleProjectIds,
    isLoadingAccessibleProjects,
    // Handlers (simple setters like CV Repository)
    setSearchInputValue,
    setSelectedBusinessAreas,
    setSelectedClients,
    setSelectedBusinessUnits,
    setSelectedProjectStatus,
    setSelectedCluster,
    setSelectedClientPartyTypes,
    setSelectedScopeTypes,
    setSelectedManager,
    setIncludeHierarchyProjects,
    clearSearchFilters,
  };
};

export default useProjectsRepositorySearch;
